import React from 'react'

import { ZariContext } from '_zari/context'

import { Alert, Form, Button, Divider, Col, Row, Table } from '_zari/core'

import { DownloadAxiosFile } from 'utils'

import { EstadosOSE } from 'constants/index'

import * as FacturacionHistorialService from 'services/facturacionHistorial.service'
import * as GuiasVentaHistorialService from 'services/guiasVentaHistorial.service'

import * as FormGroups from './formGroups'
import ConfirmacionModal from './modals/confirmacion'
import ConsultaSunatModal from './modals/consultaSunat'

const ComprobantesElectronicosTab = () => {
  const __zariContext = React.useContext(ZariContext)

  const [buscandoFechas, setBuscandoFechas] = React.useState(true)
  const [, setMostrarEnviandoCorreoModal] = React.useState(false)

  const [fechaDesde, setFechaDesde] = React.useState('')
  const [fechaHasta, setFechaHasta] = React.useState('')
  const [estado, setEstado] = React.useState(EstadosOSE.PENDIENTE)
  const [documentos, setDocumentos] = React.useState([])
  const [mostrarConfirmacionModal, setMostrarConfirmacionModal] = React.useState(false)
  const [mostrarConsultaSunatModal, setMostrarConsultaSunatModal] = React.useState(false)
  const [seleccionarTodos, setSeleccionarTodos] = React.useState(false)
  const consultaSunatInfo = React.useRef('')

  const docIdRef = React.useRef('')
  const primeraBusquedaRealizadaRef = React.useRef(false)

  const clickConsultarHandler = React.useCallback(() => {
    if (!fechaDesde) {
      return
    }

    if (!fechaHasta) {
      return
    }

    GuiasVentaHistorialService.obtenerAsEmisor({
      fechaDesde,
      fechaHasta,
      estadoOSE: estado
    }).then(({ data }) => {
      setDocumentos(
        data.map(x => {
          return {
            ...x,
            checked: false
          }
        })
      )
    })
  }, [fechaDesde, fechaHasta, estado])

  React.useEffect(() => {
    if (!primeraBusquedaRealizadaRef.current) {
      GuiasVentaHistorialService.obtenerPrimerPendienteEmisor().then(({ data }) => {
        setFechaDesde(data.fechaDesde)
        setFechaHasta(data.fechaHasta)

        clickConsultarHandler()
        primeraBusquedaRealizadaRef.current = true

        setBuscandoFechas(false)
      })
    }
  }, [clickConsultarHandler])

  const enviarAOSEClick = () => {
    const checkedDocs = documentos.filter(x => x.checked)

    if (checkedDocs.length > 0) {
      if (
        checkedDocs.filter(x => x.estadoOSE === EstadosOSE.ENPROCESO || x.estadoOSE === EstadosOSE.PROCESADO).length > 0
      ) {
        __zariContext.snack('error', 'No se puede emitir documentos con estado PENDIENTE o ACEPTADO.')
        return
      }

      docIdRef.current = checkedDocs.map(x => x._id)
      setMostrarConfirmacionModal(true)
    } else {
      __zariContext.snack('Seleccionar documentos a enviar.')
    }
  }

  const checkDocumentoChange = ({ e, _id }) => {
    setDocumentos(prev => {
      return prev.map(x => {
        return {
          ...x,
          checked: x._id === _id ? !x.checked : x.checked
        }
      })
    })
  }

  const seleccionarTodosClick = () => {
    setSeleccionarTodos(prevChecked => {
      setDocumentos(prev => {
        return prev.map(x => ({
          ...x,
          checked: !prevChecked
        }))
      })

      return !prevChecked
    })
  }

  const busquedaSunatClick = ({ _id }) => {
    FacturacionHistorialService.consultaSunatxId(_id)
      .then(({ data }) => {
        consultaSunatInfo.current = data
        setMostrarConsultaSunatModal(true)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }

  const clickEnviarCorreo = React.useCallback(
    ({ _id }) => {
      setMostrarEnviandoCorreoModal(true)
      GuiasVentaHistorialService.enviarCorreoXmlxId(_id)
        .then(() => {
          __zariContext.snack('success', 'Correo enviado!')
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => {
          setMostrarEnviandoCorreoModal(false)
        })
    },
    [__zariContext]
  )

  const clickEnviarCorreoConCdr = React.useCallback(
    ({ _id }) => {
      setMostrarEnviandoCorreoModal(true)
      GuiasVentaHistorialService.enviarCorreoXmlCdrxId(_id)
        .then(() => {
          __zariContext.snack('success', 'Correo enviado!')
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
        .finally(() => {
          setMostrarEnviandoCorreoModal(false)
        })
    },
    [__zariContext]
  )

  const clickDescargarCdr = React.useCallback(
    ({ _id }) => {
      GuiasVentaHistorialService.descargarCdrxId(_id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickDescargarXmlPrevio = React.useCallback(
    ({ _id }) => {
      __zariContext.snack('warning', 'Verificar estado de consulta.')
      // FacturacionHistorialService.descargarXmlPrevioxId(_id)
      //   .then(({ data, headers }) => {
      //     DownloadAxiosFile.downloadAsBlob(data, headers)
      //   })
      //   .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  return (
    <>
      {buscandoFechas ? (
        <Alert color='info'>Buscando documentos pendientes ...</Alert>
      ) : (
        <>
          <Form>
            <Col md={8}>
              <Row>
                <Col md={5}>
                  <FormGroups.FechaDesdeFormGroup value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                  <FormGroups.FechaHastaFormGroup value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                </Col>
                <Col md={5}>
                  <FormGroups.EstadoFormGroup value={estado} onChange={e => setEstado(e.target.value)} />
                </Col>
                <Col md={2}>
                  <Button onClick={clickConsultarHandler}>Consultar</Button>
                </Col>
              </Row>
            </Col>
          </Form>
          <Divider noMargin />
          <Row>
            <Col md={12} className='px-4'>
              <FormGroups.SeleccionarTodoFormGroup value={seleccionarTodos} onChange={seleccionarTodosClick} />
            </Col>
          </Row>
          <Row>
            <Col md={12} className='px-4 py-2'>
              <Table.ClientSide
                // ref={tablaRef}
                filas={documentos}
                headRows={[
                  { label: '-', width: 70, align: 'center' },
                  { label: 'CONSULTA', width: 100, align: 'center' },
                  { label: 'RUC', width: 130, align: 'center' },
                  { label: 'RAZ. SOCIAL', align: 'center' },
                  { label: 'TIPO', width: 70, align: 'center' },
                  { label: 'SERIE-NUM', width: 140, align: 'center' },
                  { label: 'EMITIDA', width: 110, align: 'center' },
                  { label: 'ENVIO', width: 150, align: 'center' },
                  { label: 'ESTADO', width: 110, align: 'center' },
                  { label: '', width: 30, align: 'center' }
                ]}
                rowTemplate={row => (
                  <Table.Row hover key={row._id}>
                    <Table.Cell align='center'>
                      <Form.Checkbox
                        checked={row.checked}
                        onChange={e => checkDocumentoChange({ e, _id: row._id })}
                        id={`flag-${row._id}`}
                        label=''
                      />
                    </Table.Cell>
                    <Table.Cell align='center'>
                      <Button onClick={e => busquedaSunatClick({ _id: row._id })} tiny>
                        Sunat
                      </Button>
                    </Table.Cell>
                    <Table.Cell align='center'>{row.ruc || '--'}</Table.Cell>
                    <Table.Cell>{row.razonSocial || '--'}</Table.Cell>
                    <Table.Cell align='center'>{row.tipoDocumento || '--'}</Table.Cell>
                    <Table.Cell align='center'>{`${row.serieDocumento}-${row.numeroDocumento}` || '--'}</Table.Cell>
                    <Table.Cell align='center'>{row.fechaEmision || '--'}</Table.Cell>
                    <Table.Cell align='center'>{row.fechaEnvio || '--'}</Table.Cell>
                    <Table.Cell align='center'>
                      {row.estadoOSE ? EstadosOSE.__dictionary[row.estadoOSE].nombre : '--'}
                      {row.flagAnulado ? ' / ANULADO' : ''}
                    </Table.Cell>
                    <Table.Cell>
                      {row.estadoOSE === EstadosOSE.PROCESADO ? (
                        <Table.Dropdown>
                          {({ handleClose }) => [
                            <Table.Dropdown.Item
                              key='enviar-correo'
                              handleClose={handleClose}
                              onClick={e => clickEnviarCorreo({ _id: row._id })}
                            >
                              Enviar Correo
                            </Table.Dropdown.Item>,
                            <Table.Dropdown.Item
                              key='enviar-correo-cdr'
                              handleClose={handleClose}
                              onClick={e => clickEnviarCorreoConCdr({ _id: row._id })}
                            >
                              Enviar Correo con Cdr
                            </Table.Dropdown.Item>,
                            <Table.Dropdown.Item
                              key='descargar-cdr'
                              handleClose={handleClose}
                              onClick={e => clickDescargarCdr({ _id: row._id })}
                            >
                              Descargar Cdr
                            </Table.Dropdown.Item>,
                            <Table.Dropdown.Item
                              key='descargar-xml-previo'
                              handleClose={handleClose}
                              onClick={e => clickDescargarXmlPrevio({ _id: row._id })}
                            >
                              Descargar Xml Previo
                            </Table.Dropdown.Item>
                          ]}
                        </Table.Dropdown>
                      ) : (
                        <Table.Dropdown>
                          {({ handleClose }) => [
                            <Table.Dropdown.Item
                              key='descargar-xml-previo'
                              handleClose={handleClose}
                              onClick={e => clickDescargarXmlPrevio({ _id: row._id })}
                            >
                              Descargar Xml Previo
                            </Table.Dropdown.Item>
                          ]}
                        </Table.Dropdown>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className='px-4'>
              <Button block onClick={enviarAOSEClick}>
                Enviar OSE
              </Button>
            </Col>
          </Row>
        </>
      )}

      {mostrarConfirmacionModal && (
        <ConfirmacionModal
          handleClose={e => setMostrarConfirmacionModal(false)}
          show={mostrarConfirmacionModal}
          docId={docIdRef.current}
          refreshTable={clickConsultarHandler}
        />
      )}
      {mostrarConsultaSunatModal && (
        <ConsultaSunatModal
          handleClose={e => setMostrarConsultaSunatModal(false)}
          show={mostrarConsultaSunatModal}
          mensaje={consultaSunatInfo.current.mensaje}
          codigo={consultaSunatInfo.current.codigo}
        />
      )}
    </>
  )
}

export default ComprobantesElectronicosTab
